import { KohaBiblioDto } from '../../../shared/models/koha/koha-biblio.dto';
import { BrowseIndexItem } from '../models';
import { cleanIsbd } from '../../../shared/pipes/clean-isbd';
import { API_ENDPOINTS } from '../../../core/constants/api-endpoints';

declare const require: any;

/**
 * Pure mapper class that transforms Koha DTOs into frontend display models.
 *
 * This is the single boundary between the Koha data format and the Angular
 * presentation layer. All Koha-specific field names, null handling, and
 * data normalization are contained here.
 *
 * This class is intentionally static with no dependencies — it's a pure
 * function container that's trivial to unit test.
 */
export class BiblioMapper {

  /**
   * Map a single Koha biblio DTO to a BrowseIndexItem.
   */
  static toModel(dto: KohaBiblioDto): BrowseIndexItem {
    return {
      id: dto.biblio_id,
      title: cleanIsbd(dto.title),
      subtitle: dto.subtitle ? cleanIsbd(dto.subtitle) : null,
      author: dto.author ? cleanIsbd(dto.author) : null,
      publicationDate: dto.copyright_date ?? dto.date_created ?? null,
      hijriDate: BiblioMapper.formatHijriDate(dto.copyright_date),
      isbn: dto.biblioitem?.isbn ?? null,
      itemType: dto.biblioitem?.itemtype ?? 'UNKNOWN',
      itemTypeLabel: dto._strings?.['itemtype']?.str ?? null,
      coverImageUrl: BiblioMapper.buildCoverImageUrl(dto),
      language: dto.biblioitem?.language ?? null,
      libraryId: dto.holding_library_id ?? dto.home_library_id ?? null,
      libraryName: null,  // Resolved later by joining with library data
      callNumber: dto.biblioitem?.callnumber ?? null,
      abstract: dto.abstract ?? null,
    };
  }

  /**
   * Map an array of Koha biblio DTOs to BrowseIndexItems.
   */
  static toModelList(dtos: KohaBiblioDto[]): BrowseIndexItem[] {
    if (!dtos || !Array.isArray(dtos)) {
      return [];
    }
    return dtos.map(BiblioMapper.toModel);
  }

  /**
   * Build the cover image URL from the DTO.
   * Returns null if no cover images are available.
   */
  private static buildCoverImageUrl(dto: KohaBiblioDto): string | null {
    if (dto.cover_image_ids?.length && dto.cover_image_ids[0] != null) {
      return API_ENDPOINTS.coverImage(dto.biblio_id, dto.cover_image_ids[0]);
    }
    return null;
  }

  /**
   * Format a Gregorian date string into a Hijri display string.
   *
   * Uses moment-hijri for conversion. Returns null if the input
   * date is invalid or not provided.
   *
   * Example output: "11 محرم 1446هـ"
   *
   * Note: moment-hijri is imported dynamically to keep this mapper
   * testable without heavy dependencies. In production, consider
   * using the HijriDatePipe for template formatting instead.
   */
  private static formatHijriDate(gregorianDate: string | null): string | null {
    if (!gregorianDate) {
      return null;
    }

    try {
      // Dynamically use moment-hijri if available
      // This is a placeholder — actual implementation depends on
      // how moment-hijri is configured in the project
      const moment = require('moment-hijri');
      const hijriMoment = moment(gregorianDate, 'YYYY-MM-DD');
      if (!hijriMoment.isValid()) {
        return null;
      }
      return hijriMoment.format('iD iMMMM iYYYYهـ');
    } catch {
      // Fallback: return the Gregorian date if moment-hijri fails
      return gregorianDate;
    }
  }
}
